import React from 'react';
import { Card } from 'antd';
import { PieChart, Pie, Cell, Legend, Tooltip, ResponsiveContainer } from 'recharts';
import { useTranslation } from 'react-i18next';

// 每个分块的颜色：未领取、派送失败未返仓、派送失败已返仓、派送中(#C3D9FF)、退件、丢失/破损
const COLORS = ['#1E3A8A', '#2563EB', '#60A5FA', '#C3D9FF', '#FBCFE8', '#FF4500'];

export interface SlaMonitoringPieChartProps {
  /** 饼图数据，value 可为 number 或 string（接口可能返回字符串），type 用于点击下钻 */
  data?: { name: string; value: number | string; type?: string }[];
  /** 点击某扇区时回调（仅当 value > 0 且 type 存在时触发） */
  onSliceClick?: (type: string) => void;
}

const SlaMonitoringPieChart: React.FC<SlaMonitoringPieChartProps> = ({ data, onSliceClick }) => {
  const { t } = useTranslation();
  // 使用传入数据，将 value 转为数字；过滤掉值为 0 的项；为外部标签计算占比、中角、8 扇区及扇区内序号
  const chartData: {
    name: string;
    value: number;
    type?: string;
    _percent?: number;
    _midAngle?: number;
    _angleFrom12?: number;
    _sector?: number;
    _sectorIndex?: number;
    _sectorCount?: number;
  }[] = (() => {
    const raw =
      data && data.length > 0
        ? data
            .map((item) => ({ name: item.name, value: Number(item.value) || 0, type: (item as { type?: string }).type }))
            .filter((item) => item.value > 0)
        : [];
    const total = raw.reduce((s, d) => s + d.value, 0);
    let cum = 0;
    const withAngle = raw.map((d) => {
      const p = total > 0 ? d.value / total : 0;
      const span = total > 0 ? (d.value / total) * 360 : 0;
      const midAngle = 90 - (cum + span / 2);
      cum += span;
      const angleFrom12 = (90 - midAngle + 360) % 360;
      const sector = Math.min(8, Math.floor(angleFrom12 / 45) + 1);
      return { ...d, _percent: p, _midAngle: midAngle, _angleFrom12: angleFrom12, _sector: sector };
    });
    const sectorCounts: Record<number, number> = {};
    withAngle.forEach((d) => {
      if ((d._percent ?? 0) < 0.08) sectorCounts[d._sector!] = (sectorCounts[d._sector!] ?? 0) + 1;
    });
    const sectorOrder: Record<number, number[]> = {};
    withAngle.forEach((d, i) => {
      if ((d._percent ?? 0) >= 0.08) return;
      const s = d._sector!;
      if (!sectorOrder[s]) sectorOrder[s] = [];
      sectorOrder[s].push(i);
    });
    Object.keys(sectorOrder).forEach((k) => {
      const s = Number(k);
      sectorOrder[s].sort((a, b) => (withAngle[a]._angleFrom12 ?? 0) - (withAngle[b]._angleFrom12 ?? 0));
    });
    return withAngle.map((d, i) => {
      const isExternal = (d._percent ?? 0) < 0.08;
      const sector = d._sector!;
      let idx = -1;
      if (isExternal && sectorOrder[sector]) {
        const pos = sectorOrder[sector].indexOf(i);
        idx = pos >= 0 ? pos : 0;
      }
      const count = sectorCounts[sector] ?? 0;
      return {
        ...d,
        _sectorIndex: idx,
        _sectorCount: count,
      };
    });
  })();
  const legendOrder = chartData.map((entry) => entry.name);

  return (
    <Card
      className="min-w-[420px] z-0 [&]:border-none [&_.ant-card-head]:border-none [&_.ant-card-body]:border-none [&_.ant-card-head]:h-auto [&_.ant-card-head]:min-h-0 [&_.ant-card-body]:p-0 [&:focus]:outline-none [&:focus]:shadow-none [&:focus-within]:outline-none [&:focus-within]:shadow-none [&:focus-within]:border-transparent [&:focus-within]:ring-0 [&:focus-within]:ring-offset-0"
      headStyle={{ border: 'none', margin: 0, padding: '4px 0', height: 'auto', minHeight: 0, lineHeight: 1 }}
      bodyStyle={{ border: 'none', padding: 0, paddingTop: 12 }}
      title={<div className="text-base leading-tight text-center">{t('wms.delivery.parcelSlaMonitoring.pieChartTitle')}</div>}
    >
      <div className="w-full min-w-[360px] h-[260px] overflow-visible sla-pie-chart-wrap">
        <style>{`
          .sla-pie-chart-wrap .recharts-pie-sector,
          .sla-pie-chart-wrap .recharts-sector { outline: none !important; stroke: none !important; }
          .sla-pie-chart-wrap .recharts-pie-sector:focus,
          .sla-pie-chart-wrap .recharts-sector:focus { outline: none !important; stroke: none !important; }
          .sla-pie-chart-wrap svg:focus { outline: none !important; }
          .sla-pie-chart-wrap:focus { outline: none !important; }
          .sla-pie-chart-wrap .recharts-wrapper:focus { outline: none !important; }
          .sla-pie-chart-wrap *:focus { outline: none !important; box-shadow: none !important; }
        `}</style>
        {chartData.length === 0 ? (
          <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm">
            {t('wms.delivery.parcelSlaMonitoring.noData')}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart style={{ outline: 'none' }}>
              <Pie
                data={chartData}
                dataKey="value"
                nameKey="name"
                cx="54%"
                cy="54%"
                outerRadius={75}
                stroke="none"
                startAngle={90}
                endAngle={-270}
                isAnimationActive={false}
                activeShape={{ stroke: 'none', strokeWidth: 0 }}
                onClick={(data: unknown) => {
                const item = (data as { payload?: { type?: string; value?: number } })?.payload ?? (data as { type?: string; value?: number });
                if (item?.value != null && item.value > 0 && item?.type && onSliceClick) {
                  onSliceClick(item.type);
                }
              }}
              label={({ cx, cy, midAngle, innerRadius, outerRadius, value, percent, payload }) => {
                if (midAngle === undefined || cx === undefined || cy === undefined || innerRadius === undefined || outerRadius === undefined) {
                  return null;
                }
                const RADIAN = Math.PI / 180;
                const p = (payload as { _percent?: number })?._percent ?? (percent ?? 0);
                const putInside = p >= 0.08;
                const cos = Math.cos(-midAngle * RADIAN);
                const sin = Math.sin(-midAngle * RADIAN);

                if (putInside) {
                  const radius = innerRadius + (outerRadius - innerRadius) * 0.7;
                  const x = cx + radius * cos;
                  const y = cy + radius * sin;
                  return (
                    <text
                      x={x}
                      y={y}
                      textAnchor="middle"
                      dominantBaseline="middle"
                      fill="#FFFFFF"
                      fontSize={14}
                      pointerEvents="none"
                    >
                      {value}
                    </text>
                  );
                }

                // 八分折线规则（从 12 点顺时针，每 45° 一档；第二段 1–4 水平向右、5–8 水平向左）
                // 1: 竖线向上→水平向右  2: 斜线向右上→水平向右  3: 斜线向右下→水平向右  4: 竖线向下→水平向右
                // 5: 竖线向下→水平向左  6: 斜线向左下→水平向左  7: 斜线向左上→水平向左  8: 竖线向上→水平向左
                const sector = (payload as { _sector?: number })._sector ?? 1;
                const sectorIdx = (payload as { _sectorIndex?: number })._sectorIndex ?? 0;
                const sectorCount = (payload as { _sectorCount?: number })._sectorCount ?? 1;
                const labelOffsetX = 26;
                const labelStep = 14;
                const segLen = 20;
                const sqrt2 = Math.SQRT2;
                const xA = cx + outerRadius * cos;
                const yA = cy + outerRadius * sin;

                let xTurn: number;
                let yTurn: number;
                let xC: number;
                let yC: number;
                let pathD: string;
                let textAnchor: 'start' | 'middle' | 'end';
                let dx: number;

                const stackOffset = (sectorIdx - (sectorCount - 1) / 2) * labelStep;

                if (sector === 1) {
                  xTurn = xA;
                  yTurn = yA - segLen;
                  xC = xTurn + labelOffsetX;
                  yC = yTurn + stackOffset;
                  textAnchor = 'start';
                  dx = 4;
                  pathD = `M ${xA} ${yA} L ${xTurn} ${yTurn} L ${xC} ${yTurn}`;
                } else if (sector === 2) {
                  xTurn = xA + segLen / sqrt2;
                  yTurn = yA - segLen / sqrt2;
                  xC = xTurn + labelOffsetX;
                  yC = yTurn + stackOffset;
                  textAnchor = 'start';
                  dx = 4;
                  pathD = `M ${xA} ${yA} L ${xTurn} ${yTurn} L ${xC} ${yTurn}`;
                } else if (sector === 3) {
                  xTurn = xA + segLen / sqrt2;
                  yTurn = yA + segLen / sqrt2;
                  xC = xTurn + labelOffsetX;
                  yC = yTurn + stackOffset;
                  textAnchor = 'start';
                  dx = 4;
                  pathD = `M ${xA} ${yA} L ${xTurn} ${yTurn} L ${xC} ${yTurn}`;
                } else if (sector === 4) {
                  xTurn = xA;
                  yTurn = yA + segLen;
                  xC = xTurn + labelOffsetX;
                  yC = yTurn + stackOffset;
                  textAnchor = 'start';
                  dx = 4;
                  pathD = `M ${xA} ${yA} L ${xTurn} ${yTurn} L ${xC} ${yTurn}`;
                } else if (sector === 5) {
                  xTurn = xA;
                  yTurn = yA + segLen;
                  xC = xTurn - labelOffsetX;
                  yC = yTurn + stackOffset;
                  textAnchor = 'end';
                  dx = -4;
                  pathD = `M ${xA} ${yA} L ${xTurn} ${yTurn} L ${xC} ${yTurn}`;
                } else if (sector === 6) {
                  xTurn = xA - segLen / sqrt2;
                  yTurn = yA + segLen / sqrt2;
                  xC = xTurn - labelOffsetX;
                  yC = yTurn + stackOffset;
                  textAnchor = 'end';
                  dx = -4;
                  pathD = `M ${xA} ${yA} L ${xTurn} ${yTurn} L ${xC} ${yTurn}`;
                } else if (sector === 7) {
                  xTurn = xA - segLen / sqrt2;
                  yTurn = yA - segLen / sqrt2;
                  xC = xTurn - labelOffsetX;
                  yC = yTurn + stackOffset;
                  textAnchor = 'end';
                  dx = -4;
                  pathD = `M ${xA} ${yA} L ${xTurn} ${yTurn} L ${xC} ${yTurn}`;
                } else {
                  xTurn = xA;
                  yTurn = yA - segLen;
                  xC = xTurn - labelOffsetX;
                  yC = yTurn + stackOffset;
                  textAnchor = 'end';
                  dx = -4;
                  pathD = `M ${xA} ${yA} L ${xTurn} ${yTurn} L ${xC} ${yTurn}`;
                }

                const sliceType = (payload as { type?: string })?.type;
                const canDrill = Boolean(sliceType && onSliceClick && value != null && Number(value) > 0);
                return (
                  <g pointerEvents="none">
                    <path
                      d={pathD}
                      fill="none"
                      stroke="#94a3b8"
                      strokeWidth={1.2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeOpacity={0.85}
                    />
                    <text
                      x={xC + dx}
                      y={yC}
                      textAnchor={textAnchor}
                      dominantBaseline="middle"
                      fill={canDrill ? '#40a9ff' : '#333'}
                      fontSize={12}
                      pointerEvents={canDrill ? 'auto' : 'none'}
                      cursor={canDrill ? 'pointer' : undefined}
                      tabIndex={canDrill ? -1 : undefined}
                      onClick={
                      canDrill && sliceType && onSliceClick
                        ? (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onSliceClick(sliceType);
                            (document.activeElement as HTMLElement)?.blur?.();
                            setTimeout(() => (document.activeElement as HTMLElement)?.blur?.(), 0);
                          }
                        : undefined
                    }
                    >
                      {value}
                    </text>
                  </g>
                );
              }}
              labelLine={false}
            >
              {chartData.map((entry, index) => (
                <Cell
                  key={`cell-${entry.name}-${index}`}
                  fill={COLORS[index % COLORS.length]}
                  stroke="none"
                  style={entry.type && onSliceClick ? { cursor: 'pointer' } : undefined}
                />
              ))}
            </Pie>
            <Tooltip />
            <Legend
              verticalAlign="middle"
              align="right"
              layout="vertical"
              wrapperStyle={{ right: 8, marginTop: 20 }}
              iconType="circle"
              iconSize={8}
              content={({ payload }) => {
                const sorted = [...(payload || [])].sort((a, b) => {
                  const ia = legendOrder.indexOf(a.value as string);
                  const ib = legendOrder.indexOf(b.value as string);
                  return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
                });
                return (
                  <ul className="recharts-default-legend" style={{ padding: 0, margin: 0, marginTop: 20, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {sorted.map((entry) => {
                      const dataEntry = chartData.find((d) => d.name === entry.value);
                      const canDrill = Boolean(dataEntry?.type && dataEntry?.value > 0 && onSliceClick);
                      return (
                        <li
                          key={entry.value as string}
                          className="recharts-legend-item legend-item"
                          style={{ display: 'flex', alignItems: 'center', gap: 4, marginRight: 8 }}
                        >
                          <span
                            className="recharts-legend-icon"
                            style={{
                              display: 'inline-block',
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              backgroundColor: entry.color,
                              marginRight: 4,
                            }}
                          />
                          <span
                            className={`inline-block max-w-[90px] align-middle break-words leading-tight text-purple-600 ${canDrill ? 'hover:underline cursor-pointer' : ''}`}
                            style={{ fontSize: 11 }}
                            onClick={
                              canDrill && dataEntry?.type && onSliceClick
                                ? (e) => {
                                    e.stopPropagation();
                                    onSliceClick!(dataEntry!.type!);
                                  }
                                : undefined
                            }
                            onKeyDown={
                              canDrill && dataEntry?.type && onSliceClick
                                ? (e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      onSliceClick!(dataEntry!.type!);
                                    }
                                  }
                                : undefined
                            }
                            role={canDrill ? 'button' : undefined}
                            tabIndex={canDrill ? 0 : undefined}
                          >
                            {entry.value}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                );
              }}
            />
          </PieChart>
          </ResponsiveContainer>
        )}
      </div>
    </Card>
  );
};

export default SlaMonitoringPieChart;

